import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import {
  useUpdateBillingInfo,
  BillingInfo,
} from "@/api/use-customer-billing-api";
import { AxiosError } from "axios";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form, FormField, FormLabel } from "@/components/ui/form";
import {
  AutocompleteAddress,
  GoogleAddress,
} from "@/components/address-auto-complete";

const billingAddressSchema = z.object({
  line1: z.string().min(1, "Address line 1 is required"),
  line2: z.string().optional(),
  city: z.string().min(1, "City is required"),
  province: z.string().min(1, "State/Province is required"),
  country: z.string().min(1, "Country is required"),
  postalCode: z.string().min(1, "Postal code is required"),
  location: z.object({
    lat: z.number(),
    lng: z.number(),
  }),
  plusCode: z.string().optional(),
});

type BillingAddressFormValues = z.infer<typeof billingAddressSchema>;

interface BillingAddressDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  billingInfo?: BillingInfo;
}

export function BillingAddressDialog({
  open,
  onOpenChange,
  customerId,
  billingInfo,
}: BillingAddressDialogProps) {
  const { toast } = useToast();
  const form = useForm<BillingAddressFormValues>({
    resolver: zodResolver(billingAddressSchema),
    defaultValues: billingInfo?.address
      ? {
          line1: billingInfo.address.line1,
          line2: billingInfo.address.line2 ?? "",
          city: billingInfo.address.city,
          province: billingInfo.address.province,
          country: billingInfo.address.country,
          postalCode: billingInfo.address.postalCode ?? "",
          location: billingInfo.address.location,
          plusCode: billingInfo.address.plusCode ?? "",
        }
      : undefined,
  });

  const updateBillingInfoMutation = useUpdateBillingInfo({
    onSuccess: () => {
      onOpenChange(false);
      toast({
        title: "Success",
        description: "Billing address updated successfully",
      });
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast({
        title: "Error",
        description:
          error.response?.data?.message ?? "Failed to update billing address",
        variant: "destructive",
      });
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    if (billingInfo) {
      updateBillingInfoMutation.mutate({
        customerId,
        data: values,
      });
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {billingInfo ? "Edit Billing Address" : "Add Billing Address"}
          </DialogTitle>
          <DialogDescription>
            {billingInfo
              ? "Update your billing address information below"
              : "Enter your billing address information below"}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="w-full">
              <FormLabel>Billing Address</FormLabel>
              <div className="mt-2 relative z-50">
                <FormField
                  control={form.control}
                  name="line1"
                  render={() => (
                    <AutocompleteAddress
                      onSelectedAddress={(address: GoogleAddress | null) => {
                        if (address) {
                          // For line2 changes, preserve existing location
                          const existingLocation = form.getValues(
                            "location"
                          ) ?? {
                            lat: 0,
                            lng: 0,
                          };

                          const updatedAddress = {
                            line1: address.addressComponents.line1,
                            line2: address.addressComponents.line2,
                            city: address.addressComponents.city,
                            province: address.addressComponents.state,
                            postalCode: address.addressComponents.postalCode,
                            country: address.addressComponents.country,
                            location:
                              address.location.lat === 0
                                ? existingLocation
                                : address.location,
                          };

                          // Update all fields at once
                          Object.entries(updatedAddress).forEach(
                            ([key, value]) => {
                              form.setValue(
                                key as keyof BillingAddressFormValues,
                                value,
                                {
                                  shouldValidate: address.location.lat !== 0,
                                }
                              );
                            }
                          );
                        }
                      }}
                      placeholder="Search for an address"
                      initialValue={billingInfo?.address?.line1}
                      initialAddressComponents={
                        billingInfo?.address
                          ? {
                              line1: billingInfo.address.line1,
                              line2: billingInfo.address.line2 ?? "",
                              city: billingInfo.address.city,
                              state: billingInfo.address.province,
                              country: billingInfo.address.country,
                              postalCode: billingInfo.address.postalCode ?? "",
                            }
                          : undefined
                      }
                      showEditFormByDefault={!!billingInfo}
                      readOnly={false}
                      showFullForm={true}
                    />
                  )}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={updateBillingInfoMutation.isPending}
              >
                {updateBillingInfoMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : billingInfo ? (
                  "Save Changes"
                ) : (
                  "Add Address"
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
